/**
 * Firewall.jsx — High Stakes Ready
 *
 * 3-minute pre-meeting reset. Single voice-guided MP3.
 * Flow: Intro → Playing (audio runs, no controls) → Complete
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Play, Check } from "lucide-react";
import { useWakeLock } from "../hooks/useWakeLock";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EXEC  = "#D4723A";
const TEXT  = "#e8e2d6";
const MUTED = "rgba(237,232,223,0.55)";
const DIM   = "rgba(237,232,223,0.32)";

const STEPS = [
  { name: "THE", duration: "0:20" },
  { name: "PHYSIOLOGICAL", duration: "0:40" },
  { name: "VISUAL", duration: "0:40" },
  { name: "IDENTITY", duration: "0:20" },
];

const Firewall = () => {
  const navigate = useNavigate();
  const [screen, setScreen] = useState("intro"); // intro | playing | complete
  const audioRef = useRef(null);
  const { requestWakeLock, releaseWakeLock } = useWakeLock();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      releaseWakeLock();
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, []);

  const handleStart = () => {
    setScreen("playing");
    requestWakeLock();

    const audio = new Audio("/audio/high-stakes-ready.mp3");
    audioRef.current = audio;
    audio.onended = () => {
      setScreen("complete");
      releaseWakeLock();
      saveSession();
    };
    audio.play().catch(() => {});
  };

  const saveSession = async () => {
    try {
      await axios.post(`${API}/completions`, {
        duration_seconds: 120,
        steps_completed: ["stance", "physiological_sigh", "visual_anchor", "identity_reset"],
      }, { withCredentials: true });
    } catch (e) {
      console.warn("Failed to save session:", e?.message);
    }
  };

  // ── INTRO ──────────────────────────────────────────────────
  if (screen === "intro") {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: "#0c0e12" }}>
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-4">
          <button onClick={() => navigate(-1)} data-testid="back-btn">
            <ChevronLeft className="w-6 h-6" style={{ color: DIM }} />
          </button>
          <div style={{
            fontFamily: "DM Mono, monospace", fontSize: 11, letterSpacing: "0.14em",
            color: EXEC, textTransform: "uppercase",
          }}>
            High Stakes Ready
          </div>
          <div className="w-6" />
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-8">
          <h1 style={{
            fontFamily: "DM Serif Display, serif", fontSize: 28, color: TEXT,
            marginBottom: 16, textAlign: "center",
          }}>
            High Stakes Ready
          </h1>
          <p style={{
            fontFamily: "DM Sans, sans-serif", fontSize: 14, color: MUTED,
            textAlign: "center", lineHeight: 1.7, maxWidth: 340, marginBottom: 40,
          }}>
            A 3-minute protocol to reset your state before high-pressure moments. Voice-guided for eyes-closed use.
          </p>

          {/* Step dots */}
          <div className="flex items-start gap-8 mb-12">
            {STEPS.map((s) => (
              <div key={s.name} className="flex flex-col items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: EXEC, opacity: 0.7 }} />
                <span style={{
                  fontFamily: "DM Mono, monospace", fontSize: 9, letterSpacing: "0.1em",
                  color: MUTED, textTransform: "uppercase",
                }}>{s.name}</span>
                <span style={{
                  fontFamily: "DM Mono, monospace", fontSize: 10, color: DIM,
                }}>{s.duration}</span>
              </div>
            ))}
          </div>

          {/* Play button */}
          <button
            onClick={handleStart}
            data-testid="start-protocol-btn"
            className="w-20 h-20 rounded-full flex items-center justify-center mb-4 transition-transform hover:scale-105"
            style={{ background: EXEC }}
          >
            <Play className="w-8 h-8 ml-1" style={{ color: "#0c0e12" }} fill="#0c0e12" />
          </button>
          <span style={{
            fontFamily: "DM Mono, monospace", fontSize: 11, color: DIM,
            letterSpacing: "0.06em",
          }}>
            Tap to begin
          </span>
        </div>
      </div>
    );
  }

  // ── PLAYING ────────────────────────────────────────────────
  if (screen === "playing") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: "#0c0e12" }}>
        <div style={{
          fontFamily: "DM Mono, monospace", fontSize: 11, letterSpacing: "0.14em",
          color: EXEC, textTransform: "uppercase", marginBottom: 24,
        }}>
          High Stakes Ready
        </div>
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
          style={{ background: "rgba(212,114,58,0.15)", border: "1px solid rgba(212,114,58,0.3)" }}>
          <div className="w-3 h-3 rounded-full" style={{ background: EXEC, animation: "pulse 2s ease-in-out infinite" }} />
        </div>
        <p style={{
          fontFamily: "DM Serif Display, serif", fontSize: 20, color: TEXT,
          marginBottom: 8, textAlign: "center",
        }}>
          Close your eyes
        </p>
        <p style={{
          fontFamily: "DM Sans, sans-serif", fontSize: 13, color: MUTED,
          textAlign: "center",
        }}>
          Voice guidance is playing
        </p>
        <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }`}</style>
      </div>
    );
  }

  // ── COMPLETE ───────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-8" style={{ background: "#0c0e12" }}>
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
        style={{ background: "rgba(212,114,58,0.2)" }}>
        <Check className="w-7 h-7" style={{ color: EXEC }} />
      </div>
      <div style={{
        fontFamily: "DM Mono, monospace", fontSize: 11, letterSpacing: "0.14em",
        color: EXEC, textTransform: "uppercase", marginBottom: 16,
      }}>
        Reset Complete
      </div>
      <h1 style={{
        fontFamily: "DM Serif Display, serif", fontSize: 28, color: TEXT,
        marginBottom: 12, textAlign: "center",
      }}>
        You are back online
      </h1>
      <p style={{
        fontFamily: "DM Sans, sans-serif", fontSize: 14, color: MUTED,
        textAlign: "center", lineHeight: 1.7, maxWidth: 380, marginBottom: 40,
      }}>
        The actor survives. Re-enter and play your role perfectly.
      </p>
      <button
        onClick={() => navigate("/progress")}
        data-testid="return-dashboard-btn"
        className="w-full max-w-sm py-4 rounded-xl text-center font-medium transition-all hover:opacity-90"
        style={{
          background: EXEC,
          color: "#0c0e12",
          fontFamily: "DM Sans, sans-serif",
          fontSize: 15,
        }}
      >
        Return to Dashboard
      </button>
    </div>
  );
};

export default Firewall;
